import React, { useEffect, useState } from 'react';
import LineChart from '../LineChart';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { colors } from '../../../../theme/color-palette';
import {
	convertToInternationalCurrencySystem,
	convertToTwoDecimal,
} from '../../../../helper/convertAmount';
import { convertDateFormat } from '../../../../helper/convertDate';
import {
	UNITS,
	THIS_YEAR,
	LAST_YEAR,
	ERROR_SCREEN_MSG_STORE,
	BOGLE_MEDIUM,
	REPL_IN_STOCK,
	IN_STOCK_INVENTORY,
} from '../../../../constants/constant';
import { getStockDiffPer, getPercentageDiff } from '../LineChart/util';
import ErrorScreenMessage from '../../../../components/ErrorScreenMessage/ErrorScreenMessage';
import { getChartType } from '../../../../components/Definition';
import DefinitionDrawer from '../../../../components/Definition/index';
import DownloadTrend from '../../../../components/Download/DownloadTrend';
import { chartTitleChange } from '../../../../helper/yoyUtils';
import ToggleFilter from '../../../../components/Filter/ToggleFilter';

const {
	PURPLE,
	LIGHT_GREY,
	FONT_COLOR,
	POSITIVE_GROWTH_COLOR,
	NEGATIVE_GROWTH_COLOR,
	LIGHT_PURPLE,
	COLUMN_SERIES,
} = colors;

// Styles
const useStyles = makeStyles((theme) => ({
	container: {
		width: (P) => P.chartWidth,
		border: `1px solid ${LIGHT_GREY}`,
		borderRadius: '8px',
		height: '430px',
	},
	loaderContainer: {
		height: '422px',
		display: 'flex',
	},
	circularProgress: {
		margin: 'auto',
	},
	saleChartHeader: {
		width: (P) => P.chartWidth,
		height: '63px',
		padding: '16px',
	},
	percent: {
		fontSize: '20px',
		[theme.breakpoints.down('sm')]: {
			fontSize: '12px',
		},
		fontFamily: 'Bogle-Light',
		lineHeight: '27.8px',
		fontWeight: '300',
	},
	up: {
		color: POSITIVE_GROWTH_COLOR,
	},
	down: {
		color: NEGATIVE_GROWTH_COLOR,
	},
	arrow: {
		fontSize: 'large',
		marginBottom: '-3px',
		padding: '0 0 0 5px',
	},
	yoy: {
		fontSize: '12px',
		fontFamily: 'Bogle-Light',
		color: FONT_COLOR,
		marginLeft: '2px',
	},
	dashboard: {
		display: 'flex',
	},
}));

const styles = {
	saleChartTitle: {
		textAlign: 'left',
		fontSize: '18px',
		color: FONT_COLOR,
		fontFamily: 'Bogle-Regular',
	},
	saleChartAmount: {
		textAlign: 'left',
		fontSize: '24px',
		// '& @media (min-width: 320px)': {
		// 	fontSize: '16px',
		// },
		color: FONT_COLOR,
		marginTop: '3px',
		lineHeight: '33.36px',
		fontWeight: '400',
		fontFamily: BOGLE_MEDIUM,
	},
};

export const getChartValue = (isColumn, chartTitle) => {
	return [
		{
			value: 'lyValue',
			name: LAST_YEAR,
			chartLine: '6,3',
			color: isColumn ? COLUMN_SERIES : PURPLE,
			chartType: isColumn ? 'Column' : 'Line',
			isUnit: chartTitle === 'Units',
		},
		{
			value: 'tyValue',
			name: THIS_YEAR,
			chartLine: '0',
			color: isColumn ? LIGHT_PURPLE : PURPLE,
			chartType: isColumn ? 'Column' : 'Line',
			isUnit: chartTitle === 'Units',
		},
	];
};

/* Net sale chart component */
const DashboardChart = (props) => {
	const {
		chartName,
		chartWidth,
		chartTitle,
		chartHeight,
		chartData,
		showExploreButton,
		weekFilter,
		isLoading,
		updatedWidth,
		errorMsg,
		category,
		selectedFilter,
		state,
		storeName,
		storeNum,
		item,
		itemNum,
		selectedTab,
		yAxisWithout$,
		showWeekDates,
		isReplenishment,
		showTitleInErrorContent,
		isToggleFilterNeeded,
		toggleFilterOptions,
		selectedOption,
		setSelectedOption,
		brandName,
		upcNumber,
		tab,
		kpiType,
		dataTestId,
		...rest
	} = props;

	const initialCxt = {
		isDrawerOpened: false,
		metricType: null,
	};

	const [definition, setDefinition] = useState(initialCxt);

	const classes = useStyles({
		...props,
		chartWidth,
	});
	const xAxisData = {
		date: 'busDt',
		nonDate: weekFilter === 0 ? 'busDt' : 'wmYrWkNbr',
	};
	const [totalPercent, setTotalPercent] = useState(0);
	const [thisYearTotal, setThisYearTotal] = useState(0);

	const isColumn =
		weekFilter <= 0 && (chartData?.weekData?.length || chartData?.data) <= 1;

	const chartValue = getChartValue(isColumn, chartTitle);

	const isInStock =
		chartTitle === `${REPL_IN_STOCK}` || chartTitle === IN_STOCK_INVENTORY;

	useEffect(() => {
		if (chartData?.summary) {
			const _totalPercent =
				chartData?.summary?.yoy || chartData?.summary?.diff_perc;
			setTotalPercent(_totalPercent || 0);
			const currency =
				chartTitle === UNITS ||
				chartTitle === `${REPL_IN_STOCK}` ||
				yAxisWithout$ ||
				isReplenishment ||
				selectedOption === 'Units(#)'
					? ''
					: '$';
			const _thisYearTotal =
				chartData?.summary?.total || chartData?.summary?.total_ty
					? chartData?.summary?.total || chartData?.summary?.total_ty
					: 0;
			setThisYearTotal(
				`${currency}${convertToInternationalCurrencySystem(_thisYearTotal)}`
			);
		}
	}, [chartData]);

	const parseData = (data = []) => {
		if (data !== null) {
			return data?.map((item) => {
				return {
					...item,
					busDt: convertDateFormat(item.busDt),
					excel_diff_perc: isInStock
						? getStockDiffPer(item)
						: getPercentageDiff(item),
					excel_ty_value: item.tyValue,
					excel_ly_value: item.lyValue,
					week_number: item.wmYrWkNbr,
					wmWkNbr: item.wmWkNbr,
				};
			});
		}
		return [];
	};

	const isDrillDown = chartName === 'drill-down';
	const titleStyle = !isDrillDown ? { cursor: 'pointer' } : {};
	const onTitleClickHandler = () => {
		setDefinition({
			isDrawerOpened: true,
			metricType: getChartType(chartTitle),
		});
	};

	const getChartContent = () => {
		if (!chartData?.weekData && !chartData?.data) {
			return (
				<ErrorScreenMessage
					errorMsg={errorMsg}
					chartTitle={chartTitle}
					showTitleInContent={showTitleInErrorContent}
				/>
			);
		} else {
			return (
				<>
					<Box className={classes.dashboard} data-test-id={dataTestId}>
						<Box className={classes.saleChartHeader}>
							<Typography
								style={{ ...titleStyle, ...styles.saleChartTitle }}
								onClick={onTitleClickHandler}>
								{chartTitle}
							</Typography>
							<Typography style={{ ...styles.saleChartAmount }}>
								{thisYearTotal}
								{!isReplenishment && (
									<>
										{totalPercent > 0 ? (
											<ArrowUpwardIcon
												className={`${classes.up} ${classes.arrow}`}
											/>
										) : (
											<ArrowDownwardIcon
												className={`${classes.down} ${classes.arrow}`}
											/>
										)}
									</>
								)}
								{!isReplenishment && (
									<>
										<span
											className={`${classes.percent} ${
												totalPercent > 0 ? classes.up : classes.down
											}`}>{`${chartTitleChange(
											convertToTwoDecimal(totalPercent)
										)}`}</span>
										<span className={classes.yoy}>(YoY)</span>
									</>
								)}
							</Typography>
						</Box>
						{isToggleFilterNeeded && (
							<Box style={{ display: 'flex', marginTop: 20 }}>
								<ToggleFilter
									options={toggleFilterOptions}
									selectedBtn={selectedOption}
									onChange={(value) => setSelectedOption(value)}
								/>
							</Box>
						)}
						<Box>
							<DownloadTrend
								data={parseData(chartData?.weekData || chartData?.data)}
								chartTitle={chartTitle}
								weekFilter={weekFilter}
								category={category}
								state={state}
								storeName={storeName}
								storeNum={storeNum}
								item={item}
								itemNum={itemNum}
								selectedTab={selectedTab}
								selectedFilter={selectedFilter}
								inventory={['in-stock'].includes(chartName) ? true : false}
								isReplenishment={isReplenishment}
								brandName={brandName}
								selectedBtn={selectedOption}
								upcNumber={upcNumber}
								tab={tab}
								kpiType={kpiType}
							/>
						</Box>
					</Box>
					<LineChart
						chartName={chartName}
						chartData={parseData(chartData?.weekData || chartData?.data) || []}
						chartValue={chartValue}
						chartWidth={chartWidth}
						chartHeight={chartHeight}
						weekFilter={weekFilter}
						xAxisData={xAxisData}
						yAxisMinValue={0}
						isAmount={
							['SALES_VOLUME', 'salesvolume', 'in-stock'].includes(chartName) |
							yAxisWithout$ |
							isReplenishment
								? false
								: true
						}
						inStock={isInStock}
						showExploreButton={showExploreButton}
						chartTitle={chartTitle}
						showWeekDates={showWeekDates}
						isReplenishment={isReplenishment}
					/>
				</>
			);
		}
	};
	return (
		<Box className={classes.container} {...rest} key={updatedWidth}>
			{isLoading ? (
				<Box className={classes.loaderContainer}>
					<CircularProgress className={classes.circularProgress} />
				</Box>
			) : (
				getChartContent()
			)}
			<DefinitionDrawer
				isOpen={definition.isDrawerOpened}
				metricType={definition.metricType}
				onClose={() => setDefinition(initialCxt)}
			/>
		</Box>
	);
};

DashboardChart.defaultProps = {
	chartData: {},
	chartName: 'dashboardChart',
	chartWidth: '100%',
	chartHeight: '326px',
	chartTitle: 'Dashboard Chart',
	showExploreButton: true,
	weekFilter: '',
	isLoading: false,
	errorMsg: ERROR_SCREEN_MSG_STORE,
	showWeekDates: false,
	isReplenishment: false,
	showTitleInErrorContent: true,
	isToggleFilterNeeded: false,
	selectedOption: '',
	setSelectedOption: () => {},
	toggleFilterOptions: [],
	selectedTab: 'Location',
	tab: '',
	dataTestId: '',
};

DashboardChart.propTypes = {
	chartData: PropTypes.shape({
		weekData: PropTypes.arrayOf(PropTypes.shape({})),
		data: PropTypes.arrayOf(PropTypes.shape({})),
		summary: PropTypes.shape({
			yoy: PropTypes.number,
			total: PropTypes.number,
			total_ty: PropTypes.number,
			diff_perc: PropTypes.number,
		}),
	}),
	chartHeight: PropTypes.string,
	chartWidth: PropTypes.string,
	chartName: PropTypes.string,
	chartTitle: PropTypes.string,
	showExploreButton: PropTypes.bool,
	category: PropTypes.string,
	selectedFilter: PropTypes.string,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	isLoading: PropTypes.bool,
	updatedWidth: PropTypes.number,
	errorMsg: PropTypes.string,
	state: PropTypes.string,
	storeName: PropTypes.string,
	storeNum: PropTypes.string,
	item: PropTypes.string,
	selectedTab: PropTypes.string,
	itemNum: PropTypes.string,
	yAxisWithout$: PropTypes.bool,
	showWeekDates: PropTypes.bool,
	isReplenishment: PropTypes.bool,
	showTitleInErrorContent: PropTypes.bool,
	isToggleFilterNeeded: PropTypes.bool,
	toggleFilterOptions: PropTypes.arrayOf(PropTypes.string),
	selectedOption: PropTypes.string,
	brandName: PropTypes.string,
	setSelectedOption: PropTypes.func,
	upcNumber: PropTypes.string,
	tab: PropTypes.string,
	kpiType: PropTypes.string,
	dataTestId: PropTypes.string,
};

export default React.memo(DashboardChart);
